import { forwardRef } from 'react'
import * as lucide from 'lucide-react'
import { cn } from '@/ui/compat/antd/internal/utils'

/** `PlusOutlined` -> `plus`, the modifier class put on the wrapper span. */
const toIconName = (name) =>
  name
    .replace(/(Outlined|Filled|TwoTone)$/, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()

/**
 * Builds an icon component from a lucide icon.
 *
 * The glyph is wrapped in `<span role="img" class="ui-icon ui-icon-plus">` and
 * sized from the inherited font size, so stylesheet rules (`.ui-icon`,
 * `.ui-btn .ui-icon`) and inline `style={{ fontSize: 18 }}` both apply.
 */
export const createIcon = (name, lucideName) => {
  const Glyph = lucide[lucideName]
  const iconName = toIconName(name)

  const Icon = forwardRef(
    ({ className, style, spin = false, rotate, onClick, twoToneColor, ...rest }, ref) => (
      <span
        ref={ref}
        role="img"
        aria-label={iconName}
        aria-hidden={onClick ? undefined : 'true'}
        onClick={onClick}
        className={cn(
          'ui-icon',
          `ui-icon-${iconName}`,
          'inline-flex items-center justify-center leading-none',
          spin && 'animate-spin',
          onClick && 'cursor-pointer',
          className
        )}
        style={{
          ...(rotate ? { transform: `rotate(${rotate}deg)` } : null),
          ...style,
        }}
        {...rest}
      >
        <Glyph size="1em" color={twoToneColor || 'currentColor'} aria-hidden="true" />
      </span>
    )
  )

  Icon.displayName = name
  return Icon
}

export { toIconName }
